'use client';

import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@pickleballinc/ui/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@pickleballinc/ui/components/ui/radio-group';
import { cn } from '@pickleballinc/ui/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { ActionButtons } from '@/components/molecules';
import SelectForm from '@/components/molecules/SelectForm';
import { CaptainManagerDesignationData, captainManagerDesignationSchema } from '@/entities';
import { TeamLeaguePlayerStatus } from '@/entities/TeamPlayerInfo';
import { TEAM_ACTIVITY_QUERY_KEY } from '@/hooks/constants';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useUpdatePlayerRoleMutate } from '@/hooks/player/useUpdatePlayerRoleMutate';
import { useOrganizationStore } from '@/store/organization-store';

interface Props {
	title: string;
	teamName: string;
	teamId: string;
	onClose: () => void;
	playerStatus: TeamLeaguePlayerStatus;
	playerLineupStrength: number;
	playerUuid: string;
}

export const CaptainDesignationModal = ({ title, teamName, teamId, onClose, playerStatus, playerLineupStrength, playerUuid }: Props) => {
	const updatePlayerRoleMutation = useUpdatePlayerRoleMutate();
	const queryClient = useQueryClient();
	const store = useOrganizationStore();
	const teamLeagueInfo = useTeamLeagueInfo();
	const form = useForm<CaptainManagerDesignationData>({
		resolver: zodResolver(captainManagerDesignationSchema),
		defaultValues: {
			role: playerStatus,
			lineupStrength: playerLineupStrength || undefined
		}
	});

	const { handleSubmit, control, formState } = form;

	const handleDesignationSubmit = (data: CaptainManagerDesignationData) => {
		updatePlayerRoleMutation.mutate(
			{
				organizationId: store.organizationUuid,
				teamLeagueId: store.teamLeagueUuid,
				teamLeagueTeamId: teamId,
				playerId: playerUuid,
				status: data.role,
				seasonId: teamLeagueInfo.seasonUuid,
				lineupStrength: data.lineupStrength !== null ? data.lineupStrength : undefined
			},
			{
				onSuccess: () => {
					onClose();
					queryClient.invalidateQueries({
						queryKey: [TEAM_ACTIVITY_QUERY_KEY, store.organizationUuid, store.teamLeagueUuid, teamLeagueInfo.seasonUuid, teamId]
					});
				}
			}
		);
	};

	const lineupStrengthOptions = [
		{ label: 'None', value: '0' },
		...Array.from({ length: 10 }, (_, i) => ({
			label: (i + 1).toString(),
			value: (i + 1).toString()
		}))
	];
	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<DialogContent className="max-w-[340px] rounded-lg sm:max-w-[524px] gap-6" hideClose>
				<DialogHeader className="flex w-full items-end justify-between flex-row">
					<DialogTitle>{title}</DialogTitle>
					<FontAwesomeIcon icon={faTimes} size="xl" className="text-gray-500 cursor-pointer" onClick={onClose} />
				</DialogHeader>
				<FormProvider {...form}>
					<form onSubmit={handleSubmit(handleDesignationSubmit)} autoComplete="off" className="flex flex-col gap-5">
						<p className="text-lg flex font-semibold text-gray-500">{teamName}</p>

						<div className="flex flex-col gap-6">
							<Controller
								name="role"
								control={control}
								render={({ field }) => (
									<RadioGroup className="flex flex-col gap-4" value={field.value} onValueChange={field.onChange} name="role">
										<div className="flex items-center gap-1">
											<RadioGroupItem
												id="non-captain"
												value={TeamLeaguePlayerStatus.TEAM_PLAYER}
												className="size-5 cursor-pointer"
												icon={({ className }: { className: string }) => (
													<FontAwesomeIcon icon={faCircle} className={cn('!size-2', className)} />
												)}
											/>
											<label className="text-md ml-2 cursor-pointer" htmlFor="non-captain">
												Non-captain
											</label>
										</div>

										<div className="flex  items-center">
											<RadioGroupItem
												id="captain"
												value={TeamLeaguePlayerStatus.TEAM_CAPTAIN}
												className="size-5 cursor-pointer"
												icon={({ className }: { className: string }) => (
													<FontAwesomeIcon icon={faCircle} className={cn('!size-2', className)} />
												)}
											/>
											<label className="text-md ml-2 cursor-pointer" htmlFor="captain">
												Captain
											</label>
										</div>

										<div className="flex items-center">
											<RadioGroupItem
												id="co-captain"
												value={TeamLeaguePlayerStatus.TEAM_CAPTAIN_ASSISTANCE}
												className="size-5 cursor-pointer"
												icon={({ className }: { className: string }) => (
													<FontAwesomeIcon icon={faCircle} className={cn('!size-2', className)} />
												)}
											/>
											<label className="text-md ml-2 cursor-pointer" htmlFor="co-captain">
												Co-captain
											</label>
										</div>
									</RadioGroup>
								)}
							/>
						</div>

						{teamLeagueInfo.useLineupStrength ? <hr className="h-px rounded-xl bg-brand-300" /> : null}

						{teamLeagueInfo.useLineupStrength ? (
							<SelectForm
								name="lineupStrength"
								options={lineupStrengthOptions}
								label="Lineup strength"
								placeholder="Select"
								className="w-[175px]"
								sendAsNumber
								errorMessage={formState.errors.lineupStrength?.message?.toString()}
							/>
						) : null}

						<ActionButtons
							className="justify-end"
							onClose={onClose}
							submitButtonText="Save"
							closeButtonText="Cancel"
							submitButtonDisabled={formState.isSubmitting || !formState.isDirty || updatePlayerRoleMutation.isPending}
						/>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
};
